import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import apiService from '../../services/api.service'
import { createApiCase } from 'helper/create.api.case'
import { createApiThunk } from 'helper/create.api.thunk'

export const getOrderList = createApiThunk(
    'getOrderList',
    apiService.getOrderList
)

// export const getPendingCount = createApiThunk(
//     'getPendingCount',
//     apiService.getPendingCount
// )

export const getSalesOrderTabCount = createApiThunk(
    'getSalesOrderTabCount',
    apiService.getSalesOrderTabCount
)

// Opening a sale from the listing. Deliberately NOT in the write list below: there is
// no sale on screen to cover while it is in flight, and the form fetches the new one
// by its id once it opens.
export const createSalesOrder = createApiThunk(
    'createSalesOrder',
    apiService.createSalesOrder
)

export const getSalesOrderInfo = createApiThunk(
    'getSalesOrderInfo',
    apiService.getSalesOrderInfo
)

// Ticking a Next Action to-do line. Hands back the whole sale, so the list redraws
// from the answer rather than the screen guessing what it now looks like.
export const confirmSalesOrderChecklistItem = createApiThunk(
    'confirmSalesOrderChecklistItem',
    apiService.confirmSalesOrderChecklistItem
)

export const updateSalesOrder = createApiThunk(
    'updateSalesOrder',
    apiService.updateSalesOrder
)

export const salesOrderAction = createApiThunk(
    'salesOrderAction',
    apiService.salesOrderAction
)

export const submitOrderPayment = createApiThunk(
    'submitOrderPayment',
    apiService.submitOrderPayment
)

export const requestOrderRefund = createApiThunk(
    'requestOrderRefund',
    apiService.requestOrderRefund
)

export const amendOrderPayment = createApiThunk(
    'amendOrderPayment',
    apiService.amendOrderPayment
)

export const amendOrderRefund = createApiThunk(
    'amendOrderRefund',
    apiService.amendOrderRefund
)

export const initialData = {
    orderSort: {
        active_page: 1,
        start: 0,
        length: 10,
        search: '',
        follow_up_loading: false,
        type: 'reservation',
        // Empty means the backend's own order: newest first.
        sort: '',
        order: '',
    },
    orderData: {
        list: [],
        total: 0,
        loading: false,
    },
    orderInfo: {},
    orderInfoLoading: false,
    // A save in flight, as distinct from the record being fetched - see the write
    // thunks below.
    savingSalesOrder: false,
    // One number per tab, off the endpoint that also filters the listing.
    orderTabCounts: {},
    pendingCount: {},
}

const dataSlice = createSlice({
    name: 'order/data',
    initialState: initialData,
    reducers: {
        setOrderSort: (state, action) => {
            state.orderSort = action.payload
        },
        clearOrderSort: (state, action) => {
            state.orderSort = {
                active_page: 1,
                start: 0,
                length: 10,
                search: '',
                follow_up_loading: false,
                type: 'reservation',
                sort: '',
                order: '',
            }
        },
    },
    extraReducers: (builder) => {
        createApiCase(builder, getSalesOrderTabCount, {
            fulfilled: (state, action) => {
                state.orderTabCounts = action.payload.data?.tab_counts || {}
            },
        })
        builder
            .addCase(confirmSalesOrderChecklistItem.fulfilled, (state, action) => {
                state.orderInfo = action.payload.data
            })
            .addCase(getSalesOrderInfo.pending, (state) => {
                state.orderInfoLoading = true
            })
            .addCase(getSalesOrderInfo.fulfilled, (state, action) => {
                state.orderInfoLoading = false
                state.orderInfo = action.payload.data
            })
            .addCase(getSalesOrderInfo.rejected, (state) => {
                state.orderInfoLoading = false
            })
        // Every write answers with the whole sale, so the screen re-renders from the
        // response rather than firing a second GET to find out what it just did.
        //
        // A SAVE IS NOT A LOAD. These used to raise `orderInfoLoading`, which means
        // "this is not the record yet" - so the form collapsed to a skeleton, every
        // section the operation user had open snapped shut, and the page jumped. The
        // record IS still there while a save is in flight; it is only busy. That is
        // what `savingSalesOrder` says, and the form draws a cover over itself instead.
        // The amends are here too, so all four money writes behave the same - they
        // answer with the whole sale like the rest, and the screen had been firing a
        // second GET after them purely because they were left out of this list.
        ;[
            updateSalesOrder,
            salesOrderAction,
            submitOrderPayment,
            requestOrderRefund,
            amendOrderPayment,
            amendOrderRefund,
        ].forEach(
            (thunk) => {
                builder
                    .addCase(thunk.pending, (state) => {
                        state.savingSalesOrder = true
                    })
                    .addCase(thunk.fulfilled, (state, action) => {
                        state.savingSalesOrder = false
                        if (action.payload?.data) {
                            state.orderInfo = action.payload.data
                        }
                    })
                    .addCase(thunk.rejected, (state) => {
                        state.savingSalesOrder = false
                    })
            }
        )
        // createApiCase(builder, getPendingCount, {
        //     fulfilled: (state, action) => {
        //         state.pendingCount = action.payload.data.counts
        //     },
        // })
        builder
            .addCase(getOrderList.pending, (state) => {
                state.orderData.loading = true
                // state.orderSort.follow_up_loading = false
            })
            .addCase(getOrderList.fulfilled, (state, action) => {
                state.orderData.loading = false
                // state.serviceSort.follow_up_loading = false

                state.orderData.list = action.payload.data.aaData
                state.orderData.total = action.payload.data.iTotalDisplayRecords
            })
            .addCase(getOrderList.rejected, (state) => {
                state.orderData.loading = false
                // state.orderSort.follow_up_loading = false
            })
    },
})

export const { setOrderSort, clearOrderSort } = dataSlice.actions

export default dataSlice.reducer
